/**
 * Maps Places API (New) `Place.addressComponents` onto the venue address fields used by
 * the list-your-venue signup and the venue cabinet location form.
 *
 * The legacy API exposed `address_components` with `long_name`/`short_name`; the new one
 * exposes `addressComponents` with `longText`/`shortText`.
 */

const LONG_TEXT_COMPONENTS = [
  'street_number',
  'route',
  'premise',
  'postal_town',
  'locality',
  'sublocality',
  'postal_code',
  'postal_code_prefix',
];

function componentsByType(place) {
  const byType = {};

  (place.addressComponents || []).forEach((component) => {
    component.types.forEach((type) => {
      byType[type] ??= component;
    });
  });

  return byType;
}

/**
 * @returns {{city: string, postcode: string, addressLine1: string, stateCode: string, countryCode: string}}
 *          Empty strings for anything the place does not resolve; countryCode is ISO 3166-1
 *          alpha-2 (e.g. "GB").
 */
export function parseVenueAddress(place) {
  const byType = componentsByType(place);
  const longText = type => byType[type]?.longText ?? '';

  const parts = {};
  LONG_TEXT_COMPONENTS.forEach((type) => {
    parts[type] = longText(type);
  });

  const streetName = parts.route || parts.premise || parts.locality;
  const streetNumber = parts.street_number;

  return {
    city: parts.postal_town || parts.locality || parts.sublocality,
    postcode: parts.postal_code || parts.postal_code_prefix,
    addressLine1: streetNumber && streetName ? `${streetNumber} ${streetName}` : streetName,
    stateCode: byType.administrative_area_level_1?.shortText ?? '',
    countryCode: byType.country?.shortText ?? '',
  };
}

/** Plain `{lat, lng}` for a resolved place, or `null` when the place has no location. */
export function getPlaceCoordinates(place) {
  return place.location ? place.location.toJSON() : null;
}

/**
 * Map bounds for a resolved place as consumed by the search store
 * (`{north, south, east, west, center}`), or `null` when the place has no viewport.
 */
export function getPlaceBounds(place) {
  if (!place.viewport) {
    return null;
  }

  return {
    ...place.viewport.toJSON(),
    center: place.viewport.getCenter().toJSON(),
  };
}
